import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { animalService, type AnimalDetail } from '../services/animal.service';
import './AnimalDetails.css';

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('ru-RU');
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU');
}

function formatSex(sex: number | null): string {
  if (sex === 1) return 'Самец';
  if (sex === 2) return 'Самка';
  return '—';
}

function formatWeight(value: number | null): string {
  return value != null ? `${value} кг` : '—';
}

function formatBool(value: boolean | null | undefined): string {
  if (value == null) return '—';
  return value ? 'Да' : 'Нет';
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="animal-details-row">
      <span className="animal-details-label">{label}</span>
      <span className="animal-details-value">{value ?? '—'}</span>
    </div>
  );
}

export default function AnimalDetails() {
  const { id } = useParams();
  const [animal, setAnimal] = useState<AnimalDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAnimal = () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    animalService
      .getAnimalById(id)
      .then((response) => {
        if (!response.success || !response.data) {
          setError(response.message || 'Животное не найдено');
          return;
        }
        setAnimal(response.data);
      })
      .catch(() => {
        setError('Не удалось загрузить животное');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    loadAnimal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <div className="animal-details-page">
      <div className="animal-details-container">
        <Link to="/animals" className="animal-details-back">
          ← К списку животных
        </Link>

        {isLoading && (
          <div className="animal-details-content">
            <p>Загрузка...</p>
          </div>
        )}

        {!isLoading && error && (
          <div className="animal-details-content">
            <div className="animal-details-state">
              <p>{error}</p>
              <button type="button" className="animal-details-retry-button" onClick={loadAnimal}>
                Повторить
              </button>
            </div>
          </div>
        )}

        {!isLoading && !error && animal && (
          <>
            <div className="animal-details-header">
              <div>
                <h1>{animal.name || animal.identifierNumber}</h1>
                <p className="animal-details-subtitle">№ {animal.identifierNumber}</p>
              </div>
              {animal.status && <span className="animal-details-status">{animal.status.nameRu}</span>}
            </div>

            <div className="animal-details-grid">
              <section className="animal-details-card">
                <h2>Основная информация</h2>
                <InfoRow label="Тип" value={animal.animalType?.nameRu} />
                <InfoRow label="Порода" value={animal.breed?.nameRu} />
                <InfoRow label="Линия" value={animal.line?.name} />
                <InfoRow label="Категория" value={animal.category?.nameRu} />
                <InfoRow label="Класс" value={animal.class?.nameRu} />
                <InfoRow label="Пол" value={formatSex(animal.sex)} />
                <InfoRow label="Чистопородность" value={animal.breedPurity != null ? `${animal.breedPurity}%` : null} />
                <InfoRow label="Есть рога" value={formatBool(animal.hasHorns)} />
                <InfoRow label="Племенное" value={formatBool(animal.isBreeding)} />
              </section>

              <section className="animal-details-card">
                <h2>Идентификация</h2>
                <InfoRow label="Идентификационный номер" value={animal.identifierNumber} />
                <InfoRow label="Регистрационный номер" value={animal.registrationNumber} />
                <InfoRow label="Инвентарный номер" value={animal.inventoryNumber} />
                <InfoRow label="Номер чипа" value={animal.chipNumber} />
                <InfoRow label="Ферма" value={animal.farm?.name} />
              </section>

              <section className="animal-details-card">
                <h2>Рождение и рост</h2>
                <InfoRow label="Дата рождения" value={formatDate(animal.birthDate)} />
                <InfoRow label="Место рождения" value={animal.birthPlace} />
                <InfoRow label="Вес при рождении" value={formatWeight(animal.birthWeight)} />
                <InfoRow label="Дата отъема" value={formatDate(animal.weaningDate)} />
                <InfoRow label="Вес при отъеме" value={formatWeight(animal.weaningWeight)} />
                <InfoRow label="Вес в 12 месяцев" value={formatWeight(animal.weight12m)} />
              </section>

              <section className="animal-details-card">
                <h2>Родословная</h2>
                <InfoRow
                  label="Отец"
                  value={
                    animal.father ? (
                      <Link to={`/animals/${animal.father.id}`} className="animal-details-link">
                        {animal.father.name || animal.father.identifierNumber}
                      </Link>
                    ) : null
                  }
                />
                <InfoRow
                  label="Мать"
                  value={
                    animal.mother ? (
                      <Link to={`/animals/${animal.mother.id}`} className="animal-details-link">
                        {animal.mother.name || animal.mother.identifierNumber}
                      </Link>
                    ) : null
                  }
                />
              </section>

              <section className="animal-details-card">
                <h2>Служебная информация</h2>
                <InfoRow label="Создано" value={formatDateTime(animal.createdAt)} />
                <InfoRow label="Обновлено" value={formatDateTime(animal.updatedAt)} />
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
